import React from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const DIFFICULTY_CONFIG = {
  easy: { color: 'var(--color-success)' },
  medium: { color: 'var(--color-warning)' },
  hard: { color: 'var(--color-error)' },
  extreme: { color: '#a855f7' },
};

const ConfirmCrimeModal = ({
  crime,
  onConfirm,
  onCancel,
  territoryBonus = 0,
  playerEnergy = 100,
  evidenceLevel = 0,
}) => {
  if (!crime) return null;

  const diff = DIFFICULTY_CONFIG?.[crime?.difficulty] || DIFFICULTY_CONFIG?.easy;
  const adjustedProfit = Math.round(crime?.maxProfit * (1 + territoryBonus / 100));
  const adjustedSuccess = Math.min(99, Math.round(crime?.baseSuccessRate + (crime?.skillBonus || 0) + (territoryBonus > 0 ? 3 : 0)));

  const isOnCooldown = crime?.cooldownRemaining > 0;
  const hasInsufficientEnergy = playerEnergy < (crime?.energyCost || 0);
  const isHighEvidence = evidenceLevel > 50;

  const formatCooldown = (secs) => {
    if (secs <= 0) return '';
    if (secs < 60) return `${secs}s`;
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}m ${s}s`;
  };

  const canAttempt = !isOnCooldown && !hasInsufficientEnergy;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-md rounded-2xl overflow-hidden" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', boxShadow: 'var(--glow-md)' }}>
        {/* Header */}
        <div className="p-5 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${diff?.color}18`, border: `1px solid ${diff?.color}40` }}>
              <Icon name={crime?.icon} size={24} color={diff?.color} />
            </div>
            <div>
              <h2 className="font-heading text-base font-bold text-foreground">{crime?.name}</h2>
              <p className="font-caption text-xs text-muted-foreground">Confirm criminal activity</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          <p className="text-sm text-muted-foreground leading-relaxed">{crime?.description}</p>

          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Reward Range', value: `$${crime?.minProfit?.toLocaleString()} – $${adjustedProfit?.toLocaleString()}`, color: 'var(--color-primary)', icon: 'DollarSign' },
              { label: 'Success Rate', value: `${adjustedSuccess}%`, color: 'var(--color-success)', icon: 'Target' },
              { label: 'Jail Risk', value: `${crime?.jailTime} min`, color: 'var(--color-error)', icon: 'Lock' },
              { label: 'Energy Cost', value: `${crime?.energyCost} / ${playerEnergy}`, color: hasInsufficientEnergy ? 'var(--color-error)' : 'var(--color-accent)', icon: 'Zap' },
            ]?.map((item) => (
              <div key={item?.label} className="p-3 rounded-xl" style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon name={item?.icon} size={12} color={item?.color} />
                  <span className="font-caption text-xs text-muted-foreground">{item?.label}</span>
                </div>
                <div className="font-heading text-sm font-bold" style={{ color: item?.color }}>{item?.value}</div>
              </div>
            ))}
          </div>

          {/* Cooldown warning */}
          {isOnCooldown && (
            <div className="p-3 rounded-xl flex items-center gap-2" style={{ background: 'rgba(255,184,0,0.08)', border: '1px solid rgba(255,184,0,0.2)' }}>
              <Icon name="Clock" size={14} color="var(--color-warning)" className="flex-shrink-0" />
              <p className="font-caption text-xs" style={{ color: 'var(--color-warning)' }}>
                Crime on cooldown — available in <strong>{formatCooldown(crime?.cooldownRemaining)}</strong>
              </p>
            </div>
          )}

          {/* Insufficient energy warning */}
          {hasInsufficientEnergy && (
            <div className="p-3 rounded-xl flex items-center gap-2" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
              <Icon name="Zap" size={14} color="var(--color-error)" className="flex-shrink-0" />
              <p className="font-caption text-xs" style={{ color: 'var(--color-error)' }}>
                Not enough energy. Need <strong>{crime?.energyCost}</strong>, have <strong>{playerEnergy}</strong>.
              </p>
            </div>
          )}

          {/* High evidence warning */}
          {isHighEvidence && !isOnCooldown && !hasInsufficientEnergy && (
            <div className="p-3 rounded-xl flex items-start gap-2" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
              <Icon name="AlertTriangle" size={14} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
              <p className="font-caption text-xs" style={{ color: 'var(--color-error)' }}>
                ⚠ High evidence level ({Math.round(evidenceLevel)}%). Authorities are watching — increased arrest risk.
              </p>
            </div>
          )}

          {/* Standard risk warning */}
          {!isOnCooldown && !hasInsufficientEnergy && !isHighEvidence && (
            <div className="p-3 rounded-xl flex items-start gap-2" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
              <Icon name="AlertTriangle" size={14} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
              <p className="font-caption text-xs" style={{ color: 'var(--color-error)' }}>
                Failure results in {crime?.jailTime} minutes of jail time and +{crime?.evidenceGain} evidence accumulation.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 pt-0 flex gap-3">
          <Button variant="outline" size="default" fullWidth onClick={onCancel}>Cancel</Button>
          <Button
            variant="destructive"
            size="default"
            fullWidth
            iconName={isOnCooldown ? 'Clock' : hasInsufficientEnergy ? 'Zap' : 'Skull'}
            iconPosition="left"
            onClick={() => canAttempt && onConfirm(crime)}
            disabled={!canAttempt}
          >
            {isOnCooldown
              ? `Cooldown: ${formatCooldown(crime?.cooldownRemaining)}`
              : hasInsufficientEnergy
              ? 'Not enough energy' :'Execute'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmCrimeModal;